"use client";

import { useState, useMemo, useCallback } from "react";
import Link from "next/link";
import type { BestTimeEntry } from "@/lib/conditions/best-time-data";
import { WEIGHT_PRESETS, PRESET_LABELS } from "@/lib/conditions/best-time-presets";
import {
  SPORT_HUB_CONFIG,
} from "@/lib/conditions/timing-config";
import {
  useShallowSearchParam,
  setShallowSearchParams,
} from "@/lib/hooks/use-shallow-search-param";
import {
  calcOverallScore,
  getEffectiveWeights,
  findBestWeekIndex,
  CONDITIONS_LEGEND,
  NO_SCORE_BAND,
  PRESET_DESCRIPTIONS,
} from "./best-time-utils";
import { FilterPanel, type FilterState } from "./FilterPanel";
import { WeekPicker } from "./WeekPicker";
import { ResultCard } from "./ResultCard";
import { INITIAL_VISIBLE, paginate, nextVisible } from "./pagination";
import { presentBestTimeRankings } from "@/lib/conditions/best-time-safety";

type Props = {
  entries: BestTimeEntry[];
  sport: string;
  seasonLabel: string;
};

type SortKey = "overall" | "conditions" | "quiet" | "price";

const SORT_OPTIONS: { key: SortKey; label: string; pricingOnly?: boolean }[] = [
  { key: "overall", label: "Overall score" },
  { key: "conditions", label: "Best conditions" },
  { key: "quiet", label: "Least busy" },
  { key: "price", label: "Cheapest lift pass", pricingOnly: true },
];

export function BestTimeClient({ entries, sport, seasonLabel: _seasonLabel }: Props) {
  const config = SPORT_HUB_CONFIG[sport];
  const hasPricing = config?.hasPricing ?? false;

  // Filter/sort/preset/window state lives in the URL but is purely a
  // client-side transform over the in-memory dataset. The shallow History hook
  // keeps the URL shareable without a navigation or RSC refetch, so dragging a
  // slider no longer fires a 300-450ms server round-trip per tick. Only the
  // sport toggle (a real navigation) touches the server.
  const presetKey = useShallowSearchParam("preset") ?? "balanced";
  const sortKey = (useShallowSearchParam("sort") ?? "overall") as SortKey;
  const wsParam = useShallowSearchParam("ws");
  const weParam = useShallowSearchParam("we");
  const minCParam = useShallowSearchParam("minC");
  const maxBParam = useShallowSearchParam("maxB");
  const maxLPParam = useShallowSearchParam("maxLP");
  const minAreaParam = useShallowSearchParam("minArea");
  const tagsParam = useShallowSearchParam("tags");

  const windowStart = wsParam !== null ? Number(wsParam) : null;
  const windowEnd = weParam !== null ? Number(weParam) : null;

  const [toastVisible, setToastVisible] = useState(false);

  const preset = WEIGHT_PRESETS[presetKey] ?? WEIGHT_PRESETS.balanced;
  const weights = useMemo(
    () => getEffectiveWeights(preset, hasPricing),
    [preset, hasPricing]
  );

  const maxPrice = useMemo(() => {
    let max = 0;
    for (const e of entries) {
      for (const w of e.weeks) {
        if (w.priceEur !== undefined && w.priceEur > max) max = w.priceEur;
      }
    }
    return max || 500;
  }, [entries]);

  // Filters derive straight from the URL (an absent maxLP means "no cap", i.e.
  // maxPrice), so there is no mirrored useState to keep in sync.
  const filters = useMemo<FilterState>(
    () => ({
      minConditions: minCParam !== null ? Number(minCParam) : 0,
      maxBusyness: maxBParam !== null ? Number(maxBParam) : 1.0,
      maxLiftPass: maxLPParam !== null ? Number(maxLPParam) : maxPrice,
      minPisteKm: minAreaParam !== null ? Number(minAreaParam) : 0,
      tags: tagsParam ? tagsParam.split(",").filter(Boolean) : [],
    }),
    [minCParam, maxBParam, maxLPParam, minAreaParam, tagsParam, maxPrice]
  );

  const hasWeeksWithoutScore = useMemo(
    () => entries.some((e) => e.weeks.some((w) => w.conditionsMissing)),
    [entries]
  );

  const allWeekStarts = useMemo(() => {
    if (entries.length === 0) return [];
    const longest = entries.reduce((a, b) =>
      a.weeks.length > b.weeks.length ? a : b
    );
    return longest.weeks.map((w) => w.weekStart);
  }, [entries]);

  const setPreset = useCallback((key: string) => {
    setShallowSearchParams({ preset: key === "balanced" ? null : key });
  }, []);

  const setSort = useCallback((key: string) => {
    setShallowSearchParams({ sort: key === "overall" ? null : key });
  }, []);

  const setWindow = useCallback(
    (start: number | null, end: number | null) => {
      setShallowSearchParams({
        ws: start !== null ? String(start) : null,
        we: end !== null ? String(end) : null,
      });
    },
    []
  );

  const handleFiltersChange = useCallback(
    (newFilters: FilterState) => {
      setShallowSearchParams({
        minC: newFilters.minConditions > 0 ? String(newFilters.minConditions) : null,
        maxB: newFilters.maxBusyness < 1.0 ? String(newFilters.maxBusyness) : null,
        maxLP:
          hasPricing && newFilters.maxLiftPass < maxPrice
            ? String(Math.round(newFilters.maxLiftPass))
            : null,
        minArea: newFilters.minPisteKm > 0 ? String(newFilters.minPisteKm) : null,
        tags: newFilters.tags.length > 0 ? newFilters.tags.join(",") : null,
      });
    },
    [hasPricing, maxPrice]
  );

  const handleShare = useCallback(() => {
    navigator.clipboard.writeText(window.location.href);
    setToastVisible(true);
    setTimeout(() => setToastVisible(false), 2000);
  }, []);

  const rankedEntries = useMemo(() => {
    const ws = windowStart ?? undefined;
    const we = windowEnd ?? undefined;

    const filtered = entries.filter((e) => {
      if (e.weeks.length === 0) return false;

      if (filters.tags.length > 0) {
        for (const tag of filters.tags) {
          if (!e.tags.includes(tag)) return false;
        }
      }

      if (filters.minPisteKm > 0 && (e.pisteKm ?? 0) < filters.minPisteKm) {
        return false;
      }

      const bestIdx = findBestWeekIndex(e.weeks, weights, ws, we);
      const bestWeek = e.weeks[bestIdx];

      if (bestWeek.conditions < filters.minConditions) return false;
      if (bestWeek.crowdPressure > filters.maxBusyness) return false;

      if (
        hasPricing &&
        filters.maxLiftPass < maxPrice &&
        bestWeek.priceEur !== undefined &&
        bestWeek.priceEur > filters.maxLiftPass
      ) {
        return false;
      }

      return true;
    });

    const scored = filtered.map((entry) => {
      const bestIdx = findBestWeekIndex(entry.weeks, weights, ws, we);
      const score = calcOverallScore(entry.weeks[bestIdx], weights);
      const bestWeek = entry.weeks[bestIdx];
      return { entry, bestIdx, score, bestWeek };
    });

    scored.sort((a, b) => {
      switch (sortKey) {
        case "conditions":
          return b.bestWeek.conditions - a.bestWeek.conditions;
        case "quiet":
          return a.bestWeek.crowdPressure - b.bestWeek.crowdPressure;
        case "price":
          return (a.bestWeek.priceEur ?? Infinity) - (b.bestWeek.priceEur ?? Infinity);
        default:
          return b.score - a.score;
      }
    });

    return presentBestTimeRankings(scored, sport);
  }, [entries, weights, filters, sortKey, windowStart, windowEnd, hasPricing, maxPrice, sport]);

  // Progressive disclosure. Ranking runs over the whole set (cheap, in-memory),
  // but server-rendering every card with its full week heatmap was the slow
  // part — a 5-10 MB payload and hundreds of cards to hydrate, not the scoring.
  // We paint only the top slice and reveal the rest with "Load more".
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  // Collapse back to the top slice whenever the ranking changes (preset, sort,
  // filters or week window). rankedEntries is memoised on exactly those inputs,
  // so a new reference means the ranking changed — reset during render (no extra
  // pass, no stale-count flash) rather than in an effect.
  const [prevRanked, setPrevRanked] = useState(rankedEntries);
  if (rankedEntries !== prevRanked) {
    setPrevRanked(rankedEntries);
    setVisibleCount(INITIAL_VISIBLE);
  }

  const view = paginate(rankedEntries.length, visibleCount);

  return (
    <div>
      {/* Ranking mode selector */}
      <div className="border border-border-light rounded-[16px] p-5 mb-6">
        <div className="text-[13px] font-semibold text-navy uppercase tracking-[1.2px] mb-4">
          How should we rank?
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {Object.entries(PRESET_LABELS).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setPreset(key)}
              className={`text-left rounded-[12px] p-3 transition-all ${
                presetKey === key
                  ? "bg-navy text-white"
                  : "bg-white border border-border-light hover:border-gold/30 cursor-pointer"
              }`}
            >
              <div className={`text-[14px] font-semibold ${presetKey === key ? "" : "text-navy"}`}>
                {label}
              </div>
              <div className={`text-[12px] mt-1 ${presetKey === key ? "text-white/70" : "text-text-muted"}`}>
                {PRESET_DESCRIPTIONS[key]}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Week picker */}
      {allWeekStarts.length > 0 && (
        <WeekPicker
          weekStarts={allWeekStarts}
          windowStart={windowStart}
          windowEnd={windowEnd}
          onWindowChange={setWindow}
        />
      )}

      {/* Filters */}
      <FilterPanel
        filters={filters}
        onChange={handleFiltersChange}
        sport={sport}
        hasPricing={hasPricing}
        maxPrice={maxPrice}
      />

      {/* Sort + Share + Count */}
      <div className="border-t border-border-light pt-5 mt-1">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-[13px] font-medium text-text-muted shrink-0">Sort by</span>
          {SORT_OPTIONS.filter(
            (o) => !o.pricingOnly || hasPricing
          ).map((o) => (
            <button
              key={o.key}
              onClick={() => setSort(o.key)}
              className={`px-3 py-1.5 rounded-[8px] text-[13px] font-medium whitespace-nowrap transition-colors ${
                sortKey === o.key
                  ? "bg-navy text-white"
                  : "bg-warm-bg text-text-muted hover:bg-cream"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="text-[13px] text-text-muted hidden sm:inline">
            {rankedEntries.length} destination{rankedEntries.length !== 1 ? "s" : ""}
          </span>
          <button
            onClick={handleShare}
            className="flex items-center gap-1.5 text-[13px] text-text-muted hover:text-gold font-medium transition-colors"
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
              <path d="M4.5 6.5L7 4m0 0l2.5 2.5M7 4v6.5M2.5 9v1.5a1 1 0 001 1h7a1 1 0 001-1V9" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Share
          </button>
        </div>
      </div>

      {/* Colour legend */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mb-5 text-[11px] text-text-muted">
        {CONDITIONS_LEGEND.map((band) => (
          <div key={band.label} className="flex items-center gap-1">
            <div
              className="w-3.5 h-3.5 rounded-[2px]"
              style={{ backgroundColor: band.colour }}
            />
            {band.label}
          </div>
        ))}
        {hasWeeksWithoutScore && (
          <div className="flex items-center gap-1">
            <div
              className="w-3.5 h-3.5 rounded-[2px]"
              style={{ backgroundColor: NO_SCORE_BAND.colour }}
            />
            {NO_SCORE_BAND.label}
          </div>
        )}
      </div>
      </div>

      {/* Results */}
      <div className="space-y-3">
        {rankedEntries.slice(0, view.shown).map(({
          entry,
          bestIdx,
          score,
          promotionRank,
          safetyState,
        }) => (
          <ResultCard
            key={entry.slug}
            entry={entry}
            rank={promotionRank}
            bestWeekIdx={bestIdx}
            overallScore={score}
            weights={weights}
            windowStart={windowStart ?? undefined}
            windowEnd={windowEnd ?? undefined}
            hasPricing={hasPricing}
            sport={sport}
            safetyState={safetyState}
          />
        ))}
      </div>

      {view.hasMore && (
        <button
          onClick={() => setVisibleCount((c) => nextVisible(c))}
          className="w-full mt-4 py-3.5 rounded-[12px] border border-border-light bg-white text-navy text-[14px] font-semibold hover:border-gold/40 hover:bg-warm-bg transition-colors cursor-pointer"
        >
          Load more
          <span className="text-text-muted font-normal">
            {" "}— showing {view.shown} of {rankedEntries.length}
          </span>
        </button>
      )}

      {rankedEntries.length === 0 && (
        <div className="text-center py-12 text-text-muted text-[15px]">
          No destinations match your filters. Try loosening the criteria.
        </div>
      )}

      {rankedEntries.length > 0 && (
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 mt-4 text-[13px] text-text-muted">
          <span>Also explore:</span>
          <Link href="/research/school-holidays" prefetch={false} className="text-navy font-medium hover:underline underline-offset-2">
            Crowd pressure by week
          </Link>
          <Link href="/research/directory" prefetch={false} className="text-navy font-medium hover:underline underline-offset-2">
            Full resort directory
          </Link>
        </div>
      )}

      {/* Share toast */}
      {toastVisible && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-navy text-white px-5 py-2.5 rounded-full text-[13px] font-medium shadow-lg z-50">
          Link copied to clipboard
        </div>
      )}
    </div>
  );
}
